import {
  FinTechPAPISearchEmployeesResponse,
  SearchEmployeesRequest,
} from 'src/types/admin/advancedSearchResponseStructure';
import { FinTechPAPIGetEmployeeLoginDetailsResponse } from 'src/types/admin/employeeLoginResponseStructure';
import { logger } from '../../utils/logger';
import { RestAPINames } from './clients/apiConfig';
import * as restClient from './clients/restClient';

/**
 * API name for PAPI requests
 */
const API_NAME = RestAPINames.PAPI;

/**
 * Known PAPI error codes
 */
export type PAPIErrorCode =
  | 'USER_NOT_FOUND'
  | 'INVALID_REQUEST'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'INTERNAL_ERROR'
  | 'SERVICE_UNAVAILABLE'
  | string; // Allow unknown codes for forward compatibility

/**
 * Error response structure with specific error codes
 */
export interface PAPIErrorResponse {
  error: {
    message: string;
    code: PAPIErrorCode;
  };
}

// ============================================================================
// API METHODS
// ============================================================================

/**
 * Fetches employee information by login.
 *
 * GET /v1/employee/login/{login}
 *
 * @param login - The employee login/alias to fetch information for
 * @returns Promise resolving to employee information
 * @throws Error if employee not found or API request fails
 *
 * @example
 * const employee = await getEmployeeByLogin('annunna');
 * console.log(employee.employee.basicInfo.firstName); // "Aakash"
 */
export const getEmployeeByLogin = async (login: string): Promise<FinTechPAPIGetEmployeeLoginDetailsResponse> => {
  try {
    const response = await restClient.get<FinTechPAPIGetEmployeeLoginDetailsResponse>({
      apiName: API_NAME,
      path: `/v1/employee/login/${login}?expand=job`,
    });
    return response;
  } catch (error: unknown) {
    logger.error('Error fetching employee by login:', { login, error });

    // Return stub data for local development when PAPI is unavailable
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[DEV] PAPI unavailable — returning stub data for ${login}`);
      return {
        employee: {
          basicInfo: {
            badgeColor: 'blue',
            businessTitle: 'Software Development Engineer',
            companyId: '',
            companyName: 'Amazon',
            companyUdmId: '',
            costCenterId: '',
            costCenterName: 'DaS FinTech',
            employeeStatus: 'Active',
            extendedCostCenterId: '',
            extendedCostCenterName: '',
            firstName: login,
            isPrehire: false,
            isManager: false,
            jobProfileId: '',
            jobProfileName: '',
            jobStatus: 'Active',
            lastName: '',
            legacyEmployeeId: '',
            login,
            pastTenureDays: 0,
            personId: '',
            primaryEmail: `${login}@amazon.com`,
            primaryPhone: '',
            serviceDate: '',
            tenureDays: 0,
            managerLogin: '',
            managerName: '',
            managerPersonId: '',
            workspace: '',
          },
          workLocation: {
            buildingCode: '',
            city: '',
            citySubdivision1: '',
            country: '',
            id: '',
            name: '',
            region: '',
            regionSubdivision1: '',
            timeZoneId: '',
            timezone: '',
            types: [],
          },
        },
      };
    }

    throw error;
  }
};

export const searchEmployees = async (request: SearchEmployeesRequest): Promise<FinTechPAPISearchEmployeesResponse> => {
  try {
    const response = await restClient.post<FinTechPAPISearchEmployeesResponse>({
      apiName: API_NAME,
      path: '/v1/search/advanced',
      body: request,
    });
    return response;
  } catch (error: unknown) {
    logger.error('Error searching employees:', { request, error });
    throw error;
  }
};
